import { z } from 'zod';

export const Uuid = z.uuid();
export const IsoDateTime = z.iso.datetime({ offset: true }).describe('ISO 8601 date-time (UTC).');

export const IdParams = z.object({ id: Uuid });

export const Role = z
  .enum(['admin', 'operator'])
  .describe('`admin` = Admin, `operator` = Operadora.');
export type Role = z.infer<typeof Role>;

export const OkResponse = z.object({ ok: z.literal(true) });

/** The schema without its `.default()`s (also inside pipes), keeping descriptions. */
function withoutDefaults(schema: z.core.$ZodType): z.core.$ZodType {
  if (!(schema instanceof z.ZodType)) return schema;
  let out: z.ZodType = schema;
  if (schema instanceof z.ZodDefault || schema instanceof z.ZodPrefault) {
    const inner = withoutDefaults(schema.unwrap());
    if (inner instanceof z.ZodType) out = inner;
  } else if (schema instanceof z.ZodPipe) {
    // Rebuilt only when a default was removed inside, so refinements on the pipe stay.
    const inner = withoutDefaults(schema.in);
    if (inner instanceof z.ZodType && inner !== schema.in) out = inner.pipe(schema.out);
  }
  const meta = z.globalRegistry.get(schema);
  return meta && out !== schema ? out.meta(meta) : out;
}

/**
 * PATCH body from a create body: every field optional and **no defaults**. Zod's `.partial()`
 * keeps `.default()`s, so a PATCH that omits a field would reset it; this one leaves it alone.
 */
export function patchOf<S extends z.ZodRawShape>(schema: z.ZodObject<S>) {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [key, z.optional(withoutDefaults(field))]),
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- same keys and types as .partial(), built field by field
  return z.object(shape) as unknown as ReturnType<z.ZodObject<S>['partial']>;
}
